import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import showRefreshBenchlingToast from 'nav/toasts/showRefreshBenchlingToast';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {
  RegressionConfig,
  RegressionConfigParameterConstraint,
  RegressionType,
  RegressionParameterConstraintType,
  RegressionParameterCustomization,
} from 'ui-library/charting/statistics/regression/RegressionTypes';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {assertNever, NumericInputState} from 'util/Types';

import RegressionConfigModalModelSection from './RegressionConfigModalModelSection';
import RegressionConfigModalParameterSection from './RegressionConfigModalParameterSection';
import regressionConfigModalStateIsValid from './regressionConfigModalStateIsValid';
import {
  RegressionConfigModalState,
  RegressionConfigModalParameterConstraint,
  RegressionConfigModalParameterCustomization,
} from './RegressionConfigModalTypes';

const SUPPORTED_CONFIG_VERSIONS: Array<RegressionConfig['version']> = [1];

export enum RegressionConfigModalTab {
  MODEL = 'MODEL',
  PARAMETERS = 'PARAMETERS',
}

export type ConfigModalSupportedRegressionTypes = RegressionType.LOGISTIC;
export const CONFIG_MODAL_SUPPORTED_REGRESSION_TYPES: Array<RegressionType | null> = [
  RegressionType.LOGISTIC,
];

export type RegressionConfigModalSupportedConfig = Omit<RegressionConfig, 'regressionType'> & {
  regressionType: ConfigModalSupportedRegressionTypes;
};

export function isRegressionConfigModalSupportedConfig(
  regressionConfig: RegressionConfig | null
): regressionConfig is RegressionConfigModalSupportedConfig {
  return CONFIG_MODAL_SUPPORTED_REGRESSION_TYPES.includes(regressionConfig?.regressionType ?? null);
}

export interface RegressionConfigModalProps {
  regressionConfig: RegressionConfigModalSupportedConfig;
  onRequestSubmit: (regressionConfig: RegressionConfig) => void;
  onRequestClose: () => void;
}

function numberToNumericInputState(n: number): NumericInputState {
  return {
    isValid: true,
    numericValue: n,
    value: n.toString(),
  };
}

export function regressionConstraintToModalStateConstraint(
  constraint: RegressionConfigParameterConstraint
): RegressionConfigModalParameterConstraint {
  switch (constraint.type) {
    case RegressionParameterConstraintType.NONE:
      return constraint;
    case RegressionParameterConstraintType.EQUAL:
      return {type: constraint.type, value: numberToNumericInputState(constraint.value)};
    case RegressionParameterConstraintType.LESS_THAN:
      return {type: constraint.type, upperBound: numberToNumericInputState(constraint.upperBound)};
    case RegressionParameterConstraintType.GREATER_THAN:
      return {type: constraint.type, lowerBound: numberToNumericInputState(constraint.lowerBound)};
    case RegressionParameterConstraintType.BETWEEN:
      return {
        type: constraint.type,
        lowerBound: numberToNumericInputState(constraint.lowerBound),
        upperBound: numberToNumericInputState(constraint.upperBound),
      };
    default:
      assert(false, 'Got unexpected constraint type', {constraint: assertNever(constraint)});
  }
}

export function modalStateConstraintToRegressionConfigConstraint(
  constraint: RegressionConfigModalParameterConstraint
): RegressionConfigParameterConstraint {
  switch (constraint.type) {
    case RegressionParameterConstraintType.NONE:
      return {type: constraint.type};
    case RegressionParameterConstraintType.EQUAL:
      return {type: constraint.type, value: constraint.value.numericValue!};
    case RegressionParameterConstraintType.LESS_THAN:
      return {type: constraint.type, upperBound: constraint.upperBound.numericValue!};
    case RegressionParameterConstraintType.GREATER_THAN:
      return {type: constraint.type, lowerBound: constraint.lowerBound.numericValue!};
    case RegressionParameterConstraintType.BETWEEN:
      return {
        type: constraint.type,
        lowerBound: constraint.lowerBound.numericValue!,
        upperBound: constraint.upperBound.numericValue!,
      };
    default:
      assert(false, 'Got unexpected constraint type on', {constraint: assertNever(constraint)});
  }
}

export function convertRegressionConfigParametersToParameterCustomizations(
  regressionConfigParameters: Array<RegressionParameterCustomization>
): Array<RegressionConfigModalParameterCustomization> {
  return regressionConfigParameters.map(({name, initialValue, constraint}) => ({
    name,
    constraint: regressionConstraintToModalStateConstraint(constraint),
    initialValue:
      initialValue == null
        ? {userDefined: false}
        : {
            userDefined: true,
            value: numberToNumericInputState(initialValue),
          },
  }));
}

export function convertRegressionConfigToModalState({
  version,
  parameterCustomizations,
}: RegressionConfig): RegressionConfigModalState {
  if (!SUPPORTED_CONFIG_VERSIONS.includes(version)) {
    showRefreshBenchlingToast();
    throw new Error('Got unsupported regressionConfig version');
  }

  return {
    parameterCustomizations:
      convertRegressionConfigParametersToParameterCustomizations(parameterCustomizations),
    // If any initial parameter values are user-defined, start with the Advanced Settings drawer already open.
    showAdvancedSettings: parameterCustomizations.some(({initialValue}) => initialValue != null),
  };
}

export function convertParameterCustomizationsToRegressionConfigParameters(
  parameterCustomizations: Array<RegressionConfigModalParameterCustomization>
): Array<RegressionParameterCustomization> {
  return parameterCustomizations.map(({name, initialValue, constraint}) => ({
    name,
    initialValue: initialValue.userDefined ? initialValue.value.numericValue! : null,
    constraint: modalStateConstraintToRegressionConfigConstraint(constraint),
  }));
}

export function convertModalStateToRegressionConfig(
  modalState: RegressionConfigModalState
): RegressionConfig {
  return {
    regressionType: RegressionType.LOGISTIC,
    parameterCustomizations: convertParameterCustomizationsToRegressionConfigParameters(
      modalState.parameterCustomizations
    ),
    version: 1,
  };
}

export function hasParameterCustomizations({parameterCustomizations}: RegressionConfig): boolean {
  return parameterCustomizations.some(
    (parameterCustomization) =>
      parameterCustomization.initialValue != null ||
      parameterCustomization.constraint.type !== RegressionParameterConstraintType.NONE
  );
}

export function chooseInitialTab(regressionConfig: RegressionConfig): RegressionConfigModalTab {
  const isConfigured = hasParameterCustomizations(regressionConfig);
  return isConfigured ? RegressionConfigModalTab.PARAMETERS : RegressionConfigModalTab.MODEL;
}

export default function RegressionConfigModal({
  onRequestClose,
  onRequestSubmit,
  regressionConfig,
}: RegressionConfigModalProps): JSX.Element {
  assert(isRegressionConfigModalSupportedConfig(regressionConfig), 'Got unsupported regression type', {
    regressionType: regressionConfig.regressionType,
  });

  const [tab, setTab] = useState(chooseInitialTab(regressionConfig));

  const [regressionConfigModalState, setRegressionConfigModalState] = useState(
    convertRegressionConfigToModalState(regressionConfig)
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        assert(
          regressionConfigModalStateIsValid(regressionConfigModalState),
          'Tried to submit modal with invalid state',
          {
            regressionConfigModalState,
          }
        );

        onRequestSubmit(convertModalStateToRegressionConfig(regressionConfigModalState));
      }}
      buttons={[
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: !regressionConfigModalStateIsValid(regressionConfigModalState),
        },
      ]}
      title="Regression settings"
      className={css(styles.wrapper)}
    >
      <TabContainer<RegressionConfigModalTab> activeTab={tab} onRequestTabChange={setTab}>
        <Tab tabKey={RegressionConfigModalTab.MODEL} title="Model">
          <RegressionConfigModalModelSection
            regressionType={regressionConfig.regressionType as ConfigModalSupportedRegressionTypes}
          />
        </Tab>
        <Tab tabKey={RegressionConfigModalTab.PARAMETERS} title="Parameters">
          <RegressionConfigModalParameterSection
            regressionConfigState={regressionConfigModalState}
            onChangeRegressionConfigState={setRegressionConfigModalState}
          />
        </Tab>
      </TabContainer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    ...FONT_PROXIMA_13,
  },
});
